import { URLS, createJurisdictionHeaders } from '@/data';
import { getNextPageParam } from '@/feature/bets-sbk/hooks/use-bets';
import { useEventKeys } from '@/feature/event-details-sbk/hooks/use-event';
import { useMarketCacheStore } from '@/feature/event-details-sbk/hooks/use-market-cache';
import { EventWithMarkets } from '@/feature/event-details-sbk/types';
import { PaginationMeta } from '@/types/pagination';
import { cacheControlHeader } from '@/utils/constants-platform-specific';
import { useInfiniteQuery } from '@tanstack/react-query';

export type EventsWithMarketsResponse = {
    data: EventWithMarkets[];
    meta: PaginationMeta;
};

export type EventsWithMarketsQueryParams = {
    leagueId: string;
    isLive?: boolean;
    page?: number;
    limit: number;
};

/**
 * Fetches a list of events with popular markets
 * GET request for /events-with-markets endpoint
 */
const getEventsWithMarkets = async (params: EventsWithMarketsQueryParams): Promise<EventsWithMarketsResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('league_id', params.leagueId);
    queryParams.append('limit', params.limit.toString());
    queryParams.append('page', params?.page?.toString() ?? '1');
    if (params.isLive) {
        queryParams.append('is_live', '1');
    }
    const response = await fetch(`${URLS.CHAMELON_API_URL}/events-with-markets?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
            ...createJurisdictionHeaders(),
            ...cacheControlHeader,
        },
    });

    // API returns 404 when there are no more pages
    if (response.status === 404) {
        return {
            data: [],
            meta: {
                current_page: 0,
                from: 0,
                to: 0,
                total: 0,
                last_page: 0,
            },
        };
    }

    const data: EventsWithMarketsResponse = await response.json();

    if (!response.ok) {
        throw data;
    }

    const markets = data?.data.flatMap(event => event.popular_markets.flatMap(market => market.markets));
    useMarketCacheStore.getState().actions.populate(markets);
    return data;
};

export const useInfiniteEventsWithMarkets = (params: EventsWithMarketsQueryParams, options?: { enabled?: boolean }) => {
    const eventKeys = useEventKeys();
    return useInfiniteQuery({
        queryKey: eventKeys.byLeagueWithMarkets(params),
        queryFn: ({ pageParam = 1 }) => getEventsWithMarkets({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam,
        refetchInterval: 0,
        ...options,
    });
};
